import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { DivisionConfig } from '@r1-compass-library/models';
import { IamDivision } from '@r1-compass-library/models/iam/iam-division.model';
import { DivisionConfigService } from '@r1-compass-library/services';
import { IamDivisionService } from '@r1-compass-library/services/iam-division.service';
import { Uuid } from '@r1-compass-library/utilities/uuid';
import { Observable, exhaustMap, forkJoin, map, switchMap, tap } from 'rxjs';

export type DivisionTuple = {
    uuid: string;
    iamDivision: Partial<IamDivision>;
    divisionConfig: Partial<DivisionConfig>;
};

export interface AdminDivisionStoreState {
    divisionTuples: DivisionTuple[];
    updatedTupleIds: Set<string>;
    isLoading: boolean;
}

export const initialState: AdminDivisionStoreState = {
    divisionTuples: [],
    updatedTupleIds: new Set(),
    isLoading: false,
};

@Injectable()
export class AdminDivisionStore extends ComponentStore<AdminDivisionStoreState> {
    readonly #iamDivisionService = inject(IamDivisionService);
    readonly #divisionConfigService = inject(DivisionConfigService);

    constructor() {
        super(initialState);
    }

    divisionTuples = this.selectSignal(state => state.divisionTuples);
    updatedTupleIds = this.selectSignal(state => state.updatedTupleIds);

    setIsLoading = this.updater(state => ({
        ...state,
        isLoading: true,
    }));

    setDivisionTuples = this.updater((state, loadedDivisionTuples: DivisionTuple[]) => ({
        ...state,
        divisionTuples: loadedDivisionTuples,
        isLoading: false,
    }));

    getDivisionTuples = this.effect((trigger$: Observable<void>) => {
        return trigger$.pipe(
            tap(() => {
                this.setIsLoading();
            }),
            exhaustMap(() => {
                return forkJoin([this.#iamDivisionService.findAll(), this.#divisionConfigService.findAll()]).pipe(
                    tapResponse({
                        next: res => {
                            const tuples = this.convertToDivisionTuples(res[0], res[1]);
                            this.setDivisionTuples(tuples);
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    updateTuple = this.updater((state: AdminDivisionStoreState, updatedTuple: DivisionTuple) => {
        return {
            ...state,
            divisionTuples: state.divisionTuples.map(existingTuple => {
                if (existingTuple.uuid === updatedTuple.uuid) {
                    return updatedTuple;
                }
                return existingTuple;
            }),
            updatedTupleIds: state.updatedTupleIds.add(updatedTuple.uuid),
        };
    });

    updateDivisionTuples = this.updater((state: AdminDivisionStoreState, updatedTuples: DivisionTuple[]) => {
        return {
            ...state,
            divisionTuples: state.divisionTuples
                .filter(existingTuple => !updatedTuples.map(tuple => tuple.uuid).includes(existingTuple.uuid))
                .concat(updatedTuples),
            updatedTupleIds: new Set(),
        };
    });

    addNewTuple = this.updater((state: AdminDivisionStoreState, iamDivision: Partial<IamDivision>) => {
        const newTuple = this.createNewTuple(iamDivision);
        return {
            ...state,
            divisionTuples: [...state.divisionTuples, newTuple],
            updatedTupleIds: state.updatedTupleIds.add(newTuple.uuid),
        };
    });

    saveTuples = this.effect((trigger$: Observable<void>) => {
        return trigger$.pipe(
            exhaustMap(() => {
                const requests: Observable<DivisionTuple>[] = [];
                this.divisionTuples()
                    .filter(tuple => this.updatedTupleIds().has(tuple.uuid))
                    .forEach(tuple => {
                        if (tuple.iamDivision.id && tuple.divisionConfig.id) {
                            requests.push(this.makeUpdateRequest(tuple));
                        } else {
                            requests.push(this.makeCreateRequest(tuple));
                        }
                    });
                return forkJoin(requests).pipe(
                    tapResponse({
                        next: updatedTuples => {
                            this.updateDivisionTuples(updatedTuples);
                        },
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    private makeCreateRequest(tuple: DivisionTuple): Observable<DivisionTuple> {
        return this.#iamDivisionService.create(tuple.iamDivision).pipe(
            switchMap(newIamDivision => {
                tuple.iamDivision = newIamDivision;
                tuple.divisionConfig.iamDivisionId = newIamDivision.id;
                return this.#divisionConfigService.create(tuple.divisionConfig).pipe(
                    map(newDivisionConfig => {
                        tuple.divisionConfig = newDivisionConfig;
                        return tuple;
                    })
                );
            })
        );
    }

    private makeUpdateRequest(tuple: DivisionTuple): Observable<DivisionTuple> {
        return forkJoin([
            this.#iamDivisionService.update(tuple.iamDivision.id!, tuple.iamDivision),
            this.#divisionConfigService.update(tuple.divisionConfig.id!, tuple.divisionConfig),
        ]).pipe(
            map(([iamDivision, divisionConfig]) => {
                tuple.iamDivision = iamDivision;
                tuple.divisionConfig = divisionConfig;
                return tuple;
            })
        );
    }

    private createNewTuple(iamDivision: Partial<IamDivision>): DivisionTuple {
        const divisionConfig: Partial<DivisionConfig> = {
            config: {},
            status: 'Pending',
            valid: false,
            createdBy: iamDivision.createdBy,
            updatedBy: iamDivision.updatedBy,
        };
        return {
            uuid: Uuid.generate(),
            iamDivision: iamDivision,
            divisionConfig: divisionConfig,
        };
    }

    private convertToDivisionTuples(
        iamDivisionList: IamDivision[],
        divisionConfigList: DivisionConfig[]
    ): DivisionTuple[] {
        return iamDivisionList.flatMap(iamDivision => {
            const matchingConfg = divisionConfigList?.find(config => config.iamDivisionId === iamDivision.id);
            if (matchingConfg) {
                return [
                    {
                        uuid: Uuid.generate(),
                        iamDivision: iamDivision,
                        divisionConfig: matchingConfg,
                    },
                ];
            } else {
                return [];
            }
        });
    }
}
